import { action, computed, makeObservable, observable } from 'mobx'
import { GROUP_API } from '@voicekey/api/group/group'
import { getURL, URL_TYPES } from '@voicekey/api/http'
import { TRACKER_API } from '@voicekey/api/tracker/tracker'
import { MODALITIES } from 'App.const'
import { SEVERITIES } from './messagesStore'
import { sleep } from '@voicekey/libraries/sleep/sleep'
import dynamicMessages from 'locales/dynamicMessages'
import { ExtendedError } from '@voicekey/libraries/extendedError/ExtendedError'

const url = getURL(URL_TYPES.GROUP)

export class GroupsStore {
  constructor (rootStore) {
    makeObservable(this, {
      _groups: observable,
      _isGroupsRegisterStateLoaded: observable,
      getGroupsRegisterState: action,
      getGroupPersonIds: action,
      _profile: observable,
      getActiveGroups: action,
      deleteGroup: action,
      _selectedGroupId: observable,
      selectedGroup: computed,
      toggleGroupRegisterState: action,
      updateRegisterStatusGroup: action,
      deletePersonFromGroup: action,
      selectedGroupPersons: computed,
      addPersonsToGroup: action
    })

    this.rootStore = rootStore
  }

  _groups = null

  get groups () {
    return this._groups
  }

  set groups (value) {
    this._groups = value
  }

  _isGroupsRegisterStateLoaded = false

  get isGroupsRegisterStateLoaded () {
    return this._isGroupsRegisterStateLoaded
  }

  set isGroupsRegisterStateLoaded (value) {
    this._isGroupsRegisterStateLoaded = value
  }

  async getGroupsRegisterState () {
    await this.setProfile(MODALITIES.FACE)
    await this.setProfile(MODALITIES.VOICE)
    await this.setGroupsRegisterState(MODALITIES.FACE)
    await this.setGroupsRegisterState(MODALITIES.VOICE)
    this.isGroupsRegisterStateLoaded = true
  }

  async getGroupPersonIds () {
    try {
      const response = await GROUP_API.getGroupPersons(url, this.selectedGroupId, this.rootStore.sessionStore.sessionId)
      this.selectedGroup.personIds = response.data.map(person => person.person_id)
    } catch (e) {
      this.selectedGroup.personIds = []
      this.rootStore.messagesStore.setMessage({ ...dynamicMessages.group_get_profiles_fail, info: e })
    }
  }

  async setProfile (modality) {
    try {
      const response = await TRACKER_API.getProfile(getURL(URL_TYPES.TRACKER), this.rootStore.sessionStore.sessionId, modality)
      this.profile[modality.name] = response.data.profile
    } catch (e) {
      this.rootStore.messagesStore.setMessage({ ...dynamicMessages.get_processor_profile_fail, info: e })
    }
  }

  _profile = {
    face: '',
    voice: ''
  }

  get profile () {
    return this._profile
  }

  set profile (value) {
    this._profile = value
  }

  async setGroupsRegisterState (modality) {
    const errorGroups = []
    for (const group of this.groups) {
      try {
        await this.updateRegisterStatusGroup(modality, group, true)
      } catch (e) {
        errorGroups.push(e)
      }
    }
    if (errorGroups.length) {
      this.rootStore.messagesStore.setMessage({
        ...dynamicMessages.group_update_register_status_fail,
        values: {
          itemsCount: '',
          groupNames: (
            errorGroups.length === this.groups.length
              ? ''
              : `. ${errorGroups.map(errorGroup => errorGroup.info.name).join(', ')}`
          )
        }
      })
    }
  }

  getGroupRegisterStatus (status) {
    switch (status) {
      case 'Group is not registered': {
        return false
      }
      case 'Group is registered': {
        return true
      }
      default: {
        return null
      }
    }
  }

  async getActiveGroups () {
    try {
      const response = await GROUP_API.getGroups(url, this.rootStore.sessionStore.sessionId)
      this.groups = response.data.map(group => ({
        ...group,
        isRegistered: {
          face: null,
          voice: null
        }
      }))
      if (this.groups.length) {
        this.selectedGroupId = this.groups[0].id
      }
    } catch (e) {
      this.groups = []
      this.rootStore.messagesStore.setMessage(
        { ...dynamicMessages.get_groups_fail, info: e }
      )
    }
  }

  async deleteGroup (groupId) {
    try {
      await GROUP_API.deleteGroup(url, groupId, this.rootStore.sessionStore.sessionId)
      const newGroups = this.groups.filter(group => group.id !== groupId)
      if (groupId === this.selectedGroupId) {
        this.selectedGroupId = this.groups.length > 1 ? newGroups[0].id : null
      }
      this.groups = newGroups
      this.rootStore.messagesStore.setMessage(dynamicMessages.group_delete_success, SEVERITIES.SUCCESS)
    } catch (e) {
      this.rootStore.messagesStore.setMessage({ ...dynamicMessages.group_delete_fail, info: e })
    }
  }

  _selectedGroupId = null

  get selectedGroupId () {
    return this._selectedGroupId
  }

  set selectedGroupId (value) {
    this._selectedGroupId = value
    this.rootStore.groupPersonsStore.changePage(1)
  }

  get selectedGroup () {
    return this.groups.find(group => group.id === this.selectedGroupId)
  }

  group (groupId) {
    return computed(() => this.groups.find(group => group.id === groupId))
  }

  registerRequestParameters (modality, groupId = this.selectedGroup.id) {
    return computed(() => {
      return [
        url,
        groupId,
        this.rootStore.sessionStore.sessionId,
        modality,
        this.profile[modality.name]
      ]
    })
  }

  async toggleGroupRegisterState (modality) {
    const oldRegisterStatus = this.selectedGroup.isRegistered[modality.name]
    this.selectedGroup.isRegistered[modality.name] = null
    try {
      if (oldRegisterStatus) {
        await GROUP_API.unregisterGroup(
          ...this.registerRequestParameters(modality).get()
        )
      } else {
        await GROUP_API.registerGroup(
          ...this.registerRequestParameters(modality).get()
        )
      }
      await this.updateRegisterStatusGroup(modality, this.selectedGroup)
    } catch (e) {
      this.selectedGroup.isRegistered[modality.name] = oldRegisterStatus
      this.rootStore.messagesStore.setMessage({
        ...dynamicMessages.group_change_register_status_fail,
        info: e
      })
    }
  }

  async updateRegisterStatusGroup (modality, group, throwError = false) {
    try {
      const response = await GROUP_API.checkRegisterStatusGroup(
        ...this.registerRequestParameters(modality, group.id).get()
      )
      const newStatus = this.getGroupRegisterStatus(response.data.status)
      if (newStatus !== null) {
        group.isRegistered[modality.name] = newStatus
      } else {
        await sleep(1000)
        await this.updateRegisterStatusGroup(modality, group, throwError)
      }
    } catch (e) {
      if (throwError) {
        throw new ExtendedError(e, group)
      }
      this.rootStore.messagesStore.setMessage(
        {
          ...dynamicMessages.group_update_register_status_fail,
          values: {
            itemsCount: 1,
            groupNames: ''
          },
          info: e
        }
      )
    }
  }

  async deletePersonFromGroup (personId) {
    try {
      await GROUP_API.deletePersonFromGroup(url, this.selectedGroupId, personId, this.rootStore.sessionStore.sessionId)
      this.rootStore.groupPersonsStore.getItems()
    } catch (e) {
      this.rootStore.messagesStore.setMessage({ ...dynamicMessages.group_delete_profile_fail, info: e })
    }
  }

  get selectedGroupPersons () {
    return this.rootStore.personsStore.persons ? this.rootStore.personsStore.persons.filter(person => {
      return this.selectedGroup.personIds.includes(person.id)
    }) : this.selectedGroup.personIds.map(personId => ({ id: personId }))
  }

  updatePersonsAfterAdd () {
    this.rootStore.groupPersonsStore.getItems()
  }

  async addPersonsToGroup (personIds) {
    try {
      await GROUP_API.addPersonsToGroup(url, this.selectedGroupId, { person_ids: personIds }, this.rootStore.sessionStore.sessionId)
      this.updatePersonsAfterAdd()
    } catch (e) {
      if (e.response && e.response.status === 409) {
        const results = e.response.data.result
        const failedAddPersonIds = []
        const successAddPersonIds = []
        results.forEach(personStatus => {
          (personStatus.status === 'SUCCESS' ? successAddPersonIds : failedAddPersonIds).push(personStatus.person_id)
        })
        this.updatePersonsAfterAdd()
        if (failedAddPersonIds.length) {
          this.rootStore.messagesStore.setMessage({
            ...dynamicMessages.group_add_profile_fail,
            values: {
              itemsCount: failedAddPersonIds.length,
              personIds: (
                results.length === failedAddPersonIds.length
                  ? ''
                  : `: ${failedAddPersonIds.join(', ')}`
              )
            }
          })
        }
      } else {
        this.rootStore.messagesStore.setMessage({
          ...dynamicMessages.group_add_profile_fail,
          values: {
            itemsCount: '',
            personIds: ''
          },
          info: e
        })
      }
    }
  }

  async createGroup (groupName, throwError) {
    try {
      const response = await GROUP_API.createGroup(url, { name: groupName }, this.rootStore.sessionStore.sessionId)
      const group = {
        ...response.data,
        personIds: [],
        isRegistered: {}
      }
      Object.values(MODALITIES).forEach(modality => {
        group.isRegistered[modality.name] = false
      })
      this.groups.unshift(group)
      this.selectedGroupId = group.id
      this.rootStore.messagesStore.setMessage(dynamicMessages.group_create_success, SEVERITIES.SUCCESS)
    } catch (e) {
      this.rootStore.messagesStore.setMessage({ ...dynamicMessages.group_create_fail, info: e })
      if (throwError) throw new Error()
    }
  }
}
